import { Link } from "react-router-dom";
import { termLabel, type Course } from "../api.js";
import { useAuth } from "../auth.js";

export type CourseSection = "assignments" | "playground" | "roster" | "settings" | "library";

interface CourseLike {
  id: string;
  code: string;
  name: string;
  year: number;
  semester: number;
  archived?: boolean;
}

/**
 * The header every course page shares: which course you are in, which part of
 * it you are looking at, and what you can do here.
 *
 * The three sections used to be buttons on one page that navigated away to
 * pages with headers of their own, so moving between them changed the furniture
 * and lost the course you were in. They are links now, they look the same
 * everywhere, and the one you are on is marked.
 *
 * Actions live to the right of the title, never in the tab strip: a tab is a
 * place you can be, and "Cancel" is not a place.
 *
 * Students used to get the header with no strip at all, because every section
 * behind it was staff-only and a bar with one reachable tab is not navigation.
 * The playground is the second place a student writes code, so they get a strip
 * now -- their work and their scratchpad, side by side.
 */
/**
 * Courses bucketed by academic term, preserving the order they arrived in --
 * the API sorts newest term first, and a degree reads backwards from now.
 */
function groupByTerm<T extends { year: number; semester: number }>(courses: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const c of courses) {
    const key = termLabel(c);
    const existing = groups.get(key);
    if (existing) existing.push(c);
    else groups.set(key, [c]);
  }
  return [...groups.entries()];
}

export default function CourseChrome({
  section,
  course,
  courses,
  onSelectCourse,
  actions,
  staff = true,
  title,
}: {
  section: CourseSection;
  course: CourseLike | null;
  /** Offered as a selector when the user teaches more than one. */
  courses?: Course[];
  onSelectCourse?: (courseId: string) => void;
  actions?: React.ReactNode;
  /** Staff get the sections only staff can reach: roster, library, settings. */
  staff?: boolean;
  /** Overrides the section's own name, for a page that is not exactly it. */
  title?: string;
}) {
  const defaultTitle: Record<CourseSection, string> = {
    assignments: "Assignments",
    playground: "Playground",
    roster: "Roster",
    settings: "Course settings",
    library: "Question library",
  };
  const heading = title ?? defaultTitle[section];

  // A university admin's home is the institution console, not a course, so
  // their course pages hang off /institution instead. Same tabs, right targets.
  const { user } = useAuth();
  const isAdmin = user?.role === "UNIVERSITY_ADMIN";

  // Every tab carries the course, so moving between them never loses it.
  const q = course ? `?course=${course.id}` : "";
  const home = course && isAdmin ? `/institution/courses/${course.id}` : `/assignments${q}`;

  const tabs: { key: CourseSection; label: string; to: string; enabled: boolean }[] = [
    // A scratch compiler is not part of a course, but it is the other place
    // anyone here writes code, so it sits beside the work rather than off in the
    // top bar. It carries the course only so coming back lands where you left.
    //
    // First, because it is where you start: you try something here before it is
    // worth a mark, and a beginner being taught the basics has nothing to submit
    // yet.
    { key: "playground", label: "Playground", to: `/playground${q}`, enabled: true },
    // Named for where it lands: a teacher's is the assignment list, an admin's
    // is the course overview.
    { key: "assignments", label: isAdmin ? "Course" : "Assignments", to: home, enabled: true },
    ...(staff
      ? [
          {
            key: "roster" as const,
            label: "Roster",
            to: course ? `/courses/${course.id}/roster` : home,
            enabled: !!course,
          },
          { key: "library" as const, label: "Library", to: `/library${q}`, enabled: true },
        ]
      : []),
    // The settings panel lives on the teacher dashboard, which an admin's "/"
    // does not render -- they edit a course from the institution console.
    ...(isAdmin || !staff
      ? []
      : [
          {
            key: "settings" as const,
            label: "Settings",
            to: course ? `/assignments?course=${course.id}&edit=1` : "/assignments",
            enabled: !!course,
          },
        ]),
  ];

  return (
    <div className="course-chrome">
      {/* Above the heading, not below it: these switch between sibling pages,
          and a strip tucked under "Assignments" reads as a list of things
          inside the assignments -- which is exactly what it is not. */}
      <nav className="subnav">
        {tabs.map((t) =>
          t.enabled ? (
            <Link
              key={t.key}
              to={t.to}
              className={t.key === section ? "active" : undefined}
              aria-current={t.key === section ? "page" : undefined}
            >
              {t.label}
            </Link>
          ) : (
            <span key={t.key} className="disabled">{t.label}</span>
          )
        )}
      </nav>

      <div className="between">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{heading}</h1>
          {course ? (
            <div className="muted">
              {course.code} · {course.name} · {termLabel(course)}
              {course.archived && (
                <span className="badge draft" style={{ marginLeft: 8 }}>archived</span>
              )}
            </div>
          ) : (
            <div className="muted">
              {section === "library"
                ? "Shared questions from across your institution"
                : section === "playground"
                  ? "Scratch code — nothing here is saved or graded"
                  : "No course selected"}
            </div>
          )}
        </div>
        <div className="row">
          {courses && courses.length > 1 && onSelectCourse && (
            /*
             * Grouped by term, not one flat list. A teacher runs a handful at a
             * time, but a student accumulates every course of their degree --
             * six semesters of five subjects is thirty entries, and an
             * undifferentiated dropdown of thirty is a list you read rather than
             * a control you use. The API returns newest term first, so the
             * current semester is at the top where it is wanted.
             */
            <select
              style={{ width: "auto" }}
              value={course?.id ?? ""}
              onChange={(e) => onSelectCourse(e.target.value)}
            >
              {groupByTerm(courses).map(([term, group]) => (
                <optgroup key={term} label={term}>
                  {group.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {c.name}
                      {c.archived ? " (past)" : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
          {actions}
        </div>
      </div>
    </div>
  );
}
